import React, { useState } from 'react';
import { patientsService } from '../services/patientsService';
import { useAuth } from '../hooks/useAuth';

export type WelcomeEmailType = 'welcome' | 'welcomeWithPassword' | 'welcomeMonarka' | 'welcomeMonarkaWithPassword' | 'accessReminder';

interface EmailTemplate {
  type: WelcomeEmailType;
  label: string;
  description: string;
  monarkaOnly?: boolean;
}

// Registry of available email templates. To add a new template, add an entry
// here (and its type above + in patientsService.sendWelcomeEmail).
const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    type: 'welcome',
    label: 'Bienvenida',
    description: 'Email de bienvenida sin reset de password',
  },
  {
    type: 'welcomeWithPassword',
    label: 'Bienvenida + reset password',
    description: 'Email de bienvenida con enlace para resetear el password',
  },
  {
    type: 'welcomeMonarka',
    label: 'Bienvenida Monarka',
    description: 'Email de bienvenida Monarka sin reset de password',
    monarkaOnly: true,
  },
  {
    type: 'welcomeMonarkaWithPassword',
    label: 'Bienvenida Monarka + reset password',
    description: 'Email de bienvenida Monarka con enlace para resetear el password',
    monarkaOnly: true,
  },
  {
    type: 'accessReminder',
    label: 'Recordatorio de acceso',
    description: 'Email recordando al paciente que puede acceder a la plataforma',
  },
];

interface SendTemplateEmailModalProps {
  userId: number;
  patientName: string;
  patientEmail: string;
  onClose: () => void;
}

// Two-step modal: pick an email template, confirm, and send. Closes itself
// only after the backend confirms the send; on error it stays open.
const SendTemplateEmailModal: React.FC<SendTemplateEmailModalProps> = ({
  userId,
  patientName,
  patientEmail,
  onClose,
}) => {
  const { user } = useAuth();
  const [selected, setSelected] = useState<EmailTemplate | null>(null);
  const [isSending, setIsSending] = useState(false);

  const canSendMonarkaEmails =
    user?.role === 'admin' ||
    (user?.role === 'doctor' && user?.email?.toLowerCase().endsWith('@monarkaclinic.com'));

  const templates = EMAIL_TEMPLATES.filter(t => !t.monarkaOnly || canSendMonarkaEmails);

  const handleSend = async () => {
    if (!selected) return;
    setIsSending(true);
    try {
      await patientsService.sendWelcomeEmail(userId, selected.type);
      onClose();
    } catch (error) {
      console.error('Error sending welcome email:', error);
      alert('Error al enviar el email. Por favor, intenta nuevamente.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (!isSending) onClose();
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '28px',
          maxWidth: '480px',
          width: '90%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {!selected ? (
          <>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: 600, color: '#222' }}>
              Enviar email desde plantilla
            </h3>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#6b7280' }}>
              Para <strong>{patientName}</strong> ({patientEmail})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {templates.map((template) => (
                <button
                  key={template.type}
                  onClick={() => setSelected(template)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '10px',
                    background: '#fff',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background 0.2s, border-color 0.2s',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = '#38b2ac';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = '#fff';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{ fontSize: '15px', fontWeight: 600, color: '#222' }}>
                    {template.label}
                  </div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: 2 }}>
                    {template.description}
                  </div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  background: '#fff',
                  color: '#555',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onClick={onClose}
                onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#fff')}
              >
                Cancelar
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: 600, color: '#222' }}>
              Confirmar envío
            </h3>
            <p style={{ margin: '0 0 24px 0', fontSize: '15px', color: '#555', lineHeight: 1.5 }}>
              ¿Estás seguro de que deseas enviar el email <strong>{selected.label}</strong>
              {' '}a <strong>{patientName}</strong> ({patientEmail})?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #ddd',
                  background: '#fff',
                  color: '#555',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onClick={() => setSelected(null)}
                disabled={isSending}
                onMouseOver={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#fff')}
              >
                Volver
              </button>
              <button
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#38b2ac',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '15px',
                  cursor: isSending ? 'not-allowed' : 'pointer',
                  opacity: isSending ? 0.6 : 1,
                  transition: 'opacity 0.2s, background 0.2s',
                }}
                onClick={handleSend}
                disabled={isSending}
                onMouseOver={(e) => {
                  if (!isSending) e.currentTarget.style.background = '#319795';
                }}
                onMouseOut={(e) => {
                  if (!isSending) e.currentTarget.style.background = '#38b2ac';
                }}
              >
                {isSending ? 'Enviando...' : 'Confirmar'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SendTemplateEmailModal;
